import { CloseOutlined } from "@mui/icons-material";
import { LoadingButton } from "@mui/lab";
import {
  Autocomplete,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  InputLabel,
  Stack,
  Step,
  StepLabel,
  Stepper,
  TextField,
  Typography,
  useTheme,
} from "@mui/material";
import axios from "axios";
import React, { useContext, useEffect, useState } from "react";
import toast from "react-hot-toast";
import { jobsApi } from "src/api/jobs";
import { EMAIL_SERVER_SLAVE } from "src/config";
import { AuthContext } from "src/contexts/auth/firebase-context";
import { useColorCodes } from "src/hooks/use-color-codes";

const SendTestEmail = ({ setDialogOpen, dialogOpen, mailboxId }) => {
  const theme = useTheme();
  const colorCodes = useColorCodes();
  const { user } = useContext(AuthContext);
  const [sending, setSending] = useState(false);
  const [testMailAccounts, setTestMailAccounts] = useState([]);
  const [testEmailError, setTestEmailError] = useState("");
  const [invalidEmails, setInvalidEmails] = useState([]);
  const [testEmailInput, setTestEmailInput] = useState("");
  const [userCampaigns, setUserCampaigns] = useState([]);
  const [userCampaignsLoading, setUserCampaignsLoading] = useState(false);
  const [selectedCampaign, setSelectedCampaign] = useState(null);
  const [aiAgents, setaiAgents] = useState([]);
  const [step, setStep] = useState(0);
  const [completed, setCompleted] = React.useState({});
  const handleTestMailDialogClose = () => {
    setDialogOpen(false);
    setTestMailAccounts([]);
    setTestEmailError("");
    setTestEmailInput("");
    setInvalidEmails([]);
    setCompleted({});
    setSelectedCampaign(null);
    setStep(0);
  };
  useEffect(() => {
    if (!dialogOpen) {
      setTestEmailInput("");
    }
  }, [dialogOpen]);
  const validateEmail = (email) => {
    const regex = /^[a-zA-Z0-9+_.-]+@[a-zA-Z0-9.-]+$/;
    return regex.test(email);
  };
  const handleInputChange = (e) => {
    const typedValue = e.target.value;
    // if (typedValue.trim() === "") {
    //   return;
    // }
    setTestEmailInput(typedValue); // <-- update input state

    if (typedValue.trim() === "") {
      setTestMailAccounts([]);
      setInvalidEmails([]);
      return;
    }

    const newValue = typedValue.split(",").map((e) => e.trim());
    const unique = [...new Set(newValue)];
    const validEmails = unique.filter((email) => validateEmail(email));
    const invalidEmails = unique.filter((email) => !validateEmail(email));
    setTestMailAccounts(validEmails);
    setInvalidEmails(invalidEmails);
  };
  const sendTestEmails = async () => {
    if (testMailAccounts.length === 0) {
      setTestEmailError("Please enter the emails!");
    } else {
      try {
        setSending(true);
        setTestEmailError("");
        const baseUrl = `${EMAIL_SERVER_SLAVE}/api/v1/main`;

        const url =
          parseInt(selectedCampaign?.version) >= 2
            ? `${baseUrl}/sendTestEmail_sequence`
            : `${baseUrl}/sendTestEmail_v2`;

        const res = await axios.post(url, {
          campaignId: selectedCampaign?._id,
          mailboxId: mailboxId, // this is _id of mailbox
          recipients: testMailAccounts,
        });

        if (res.data.status === "SUCCESS") {
          setSending(false);
          toast.success("Email Sent Successfully!", {
            autoClose: 3000,
          });
          handleTestMailDialogClose();
        } else {
          setSending(false);
          //console.log("Backend error response:", res.data);
          const errorContent = res.data?.error || res.data?.message || "Failed To Connect";
          const isInvalidGrant = typeof errorContent === 'string' && errorContent.includes("invalid_grant");

          toast.error(
            isInvalidGrant
              ? "Your mailbox connection has expired. Please reconnect your mailbox to continue sending and syncing emails."
              : errorContent,
            {
              autoClose: 3000,
              style: {
                whiteSpace: "normal",
                wordBreak: "break-word",
                maxWidth: "320px",
              },
            }
          );
          handleTestMailDialogClose();
        }
      } catch (err) {
        setSending(false);
        //console.log("Backend error :", err);
        const errorData = err?.response?.data;
        const errorContent = errorData?.error || errorData?.message || err?.message || "Failed To Connect";
        const isInvalidGrant = typeof errorContent === 'string' && errorContent.includes("invalid_grant");

        toast.error(
          isInvalidGrant
            ? "Your mailbox connection has expired. Please reconnect your mailbox to continue sending and syncing emails."
            : errorContent,
          {
            autoClose: 3000,
            style: {
              whiteSpace: "normal",
              wordBreak: "break-word",
              maxWidth: "320px",
            },
          }
        );
        handleTestMailDialogClose();
      }
    }
  };
  useEffect(() => {
    if (Array.isArray(userCampaigns)) {
      // const filteredCampaigns = userCampaigns?.filter(
      //   (campaign) => !campaign?.archived
      // );
      // const filteredArchiveCampaigns = userCampaigns?.filter(
      //   (campaign) => campaign?.archived
      // );
      // const combinedCampaigns = [
      //   ...filteredCampaigns,
      //   ...filteredArchiveCampaigns,
      // ];
      setaiAgents([...userCampaigns]);
    } else {
      setaiAgents([]);
    }
  }, [userCampaigns]);
  useEffect(() => {
    if (!dialogOpen) return; 
    
    setUserCampaignsLoading(true);
    jobsApi
      .getUserCampaigns(
        user?.email,
        "",
        0,
        0,
        "all",
        "",
        "",
        "_id, status2, responsibility, archived, version"
      )
      .then((res) => {
        if (Array.isArray(res?.data)) {
          const mappedCampaigns = res?.data?.map((camp) => ({
            _id: camp?._id,
            responsibility: camp?.responsibility,
            archived: camp?.archived,
            version: camp?.version,
            status2: camp?.status2
          }));
          setUserCampaigns(mappedCampaigns);
        }
        setUserCampaignsLoading(false);
      })
      .catch((error) => {
        setUserCampaignsLoading(false);
      });
  }, [dialogOpen]); 
  const handleCampaignChange = (event, newValue) => {
    //console.log("value", newValue);
    setSelectedCampaign(newValue);
  };
  const steps = [
    {
      label: "Recipient",
      completed: testMailAccounts.length > 0,
    },
    {
      label: "Campaign",
      completed: Boolean(selectedCampaign?._id),
    },
  ];

  return (
    <Dialog
      open={dialogOpen}
      onClose={handleTestMailDialogClose}
      maxWidth="xs"
      fullWidth
    >
      <DialogTitle
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          gap: 1,
        }}
      >
        <Typography variant="h6" sx={{ color: "text.primary " }}>
          Send test email
        </Typography>
        <IconButton onClick={handleTestMailDialogClose}>
          <CloseOutlined sx={{ color: "text.primary" }} />
        </IconButton>
      </DialogTitle>
      <DialogContent>
        <Stepper activeStep={step} sx={{ mb: 2 }}>
          {steps?.map((stepItem) => {
            return (
              <Step key={stepItem.label} completed={stepItem.completed}>
                <StepLabel>{stepItem.label}</StepLabel>
              </Step>
            );
          })}
        </Stepper>
        {step === 0 ? (
          <Stack rowGap={2}>
            <Typography
              sx={{
                color: "text.primary",
                fontSize: "14px",
                fontWeight: "500",
                color: theme.palette.text.primary,
              }}
            >
              Enter recipient email address
            </Typography>

            <TextField
              variant="outlined"
              className="glass-input"
              error={!!testEmailError}
              helperText={testEmailError}
              placeholder="a@x.com,b@y.com"
              value={testEmailInput}
              onChange={handleInputChange}
            />
            {invalidEmails.length > 0 && (
              <Typography sx={{ color: theme.palette.text.primary }}>
                Invalid emails: <span>{invalidEmails.join(", ")}</span>
              </Typography>
            )}
          </Stack>
        ) : step === 1 ? (
          <>
            <Stack rowGap={2}>
              <Typography
                sx={{
                  color: "text.primary",
                  fontSize: "14px",
                  fontWeight: "500",
                  color: theme.palette.text.primary,
                }}
              >
                Choose a campaign for your email content
              </Typography>

              <Autocomplete
                fullWidth
                size="small"
                options={aiAgents || []}
                disableCloseOnSelect
                getOptionLabel={(option) => option.responsibility}
                renderOption={(props, option) => {
                  const {color: chipColor, classes} = colorCodes?.getColorCodes(option?.status2)
                  return (
                  <li
                    {...props}
                    style={{ fontFamily: "Inter", fontSize: "14px" }}
                    key={option?._id}
                  >
                    <Stack
                      flexDirection={"row"}
                      justifyContent={"space-between"}
                      alignItems={"center"}
                      gap={1}
                      sx={{ width: "100%" }}
                    >
                      {option.responsibility}
                      <Chip 
                        label={option?.status2} 
                        sx={{backgroundColor: chipColor, color: "#fff"}} 
                        className={classes.chip}
                        size="small"
                      />
                    </Stack>
                  </li>
                )}}
                style={{
                  minWidth: "160px",
                }}
                sx={{
                  "& .MuiOutlinedInput-root": {
                    paddingRight: "65px",
                    height: "auto",
                    minHeight: "40px",
                    maxHeight: "120px",
                    overflow: "auto",
                    "& .MuiOutlinedInput-notchedOutline": {
                      borderColor: "#E0E0E0",
                    },
                    "& .MuiAutocomplete-endAdornment": {
                      right: "8px",
                      position: "absolute",
                      top: "8px",
                      paddingLeft: "8px",
                    },
                    "&::-webkit-scrollbar": {
                      width: "6px",
                    },
                    "&::-webkit-scrollbar-track": {
                      background: "#f1f1f1",
                      borderRadius: "3px",
                    },
                    "&::-webkit-scrollbar-thumb": {
                      background: "#888",
                      borderRadius: "3px",
                      "&:hover": {
                        background: "#757575",
                      },
                    },
                  },
                  "& .MuiAutocomplete-tag": {
                    margin: "2px",

                    maxWidth: "calc(100% - 20px)",
                  },
                  "& .MuiInputBase-root": {
                    display: "flex",
                    flexWrap: "wrap",
                    alignContent: "flex-start",
                  },
                  "& .MuiAutocomplete-inputRoot": {
                    flexWrap: "wrap",
                  },
                  "& .MuiAutocomplete-input": {
                    width: "0",
                    minWidth: "30px",
                    flexGrow: 1,
                  },
                }}
                value={selectedCampaign}
                onChange={handleCampaignChange}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    variant="outlined"
                    className="glass-input"
                    placeholder="Select a campaign"
                    PopperProps={{
                      style: {
                        overflowY: "auto",
                      },
                    }}
                    InputProps={{
                      ...params.InputProps,
                      sx: {
                        height: "42px",
                        paddingLeft: "12px",
                      },
                    }}
                    style={{
                      minHeight: "30px",
                      width: "100%",
                    }}
                  />
                )}
              />
            </Stack>
          </>
        ) : (
          <></>
        )}
      </DialogContent>
      {}
      <DialogActions sx={{ justifyContent: "right", marginRight: "14px" }}>
        {step === 0 ? (
          <Stack
            flexDirection={"row"}
            justifyContent={"center"}
            alignItems={"center"}
            gap={1}
          >
            <Button
              variant="outlined"
              className="glass-button-secondary-50"
              onClick={() => {
                setTestMailAccounts([]);
                setInvalidEmails([]);
                setTestEmailInput("");
                setCompleted({
                  ...completed,
                  [step]: false,
                });
              }}
            >
              Clear All
            </Button>
            <Button
              variant="contained"
              disabled={testMailAccounts.length === 0}
              sx={{ width: "100px" }}
              onClick={() => {
                setStep(1);
                setCompleted({
                  ...completed,
                  [step]: true,
                });
              }}
            >
              Next
            </Button>
          </Stack>
        ) : step === 1 ? (
          <Stack
            flexDirection={"row"}
            justifyContent={"center"}
            alignItems={"center"}
            gap={1}
          >
            <Button
              variant="outlined"
              className="glass-button-secondary-50"
              onClick={() => {
                setStep(0);
              }}
            >
              Back
            </Button>
            <LoadingButton
              variant="contained"
              disabled={!selectedCampaign?._id}
              sx={{ width: "100px" }}
              onClick={sendTestEmails}
              loading={sending}
            >
              Send
            </LoadingButton>
          </Stack>
        ) : (
          <></>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default SendTestEmail;
